import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Package, ShoppingCart, Twitter, Link2, Share2, BellRing } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Seo from "@/components/Seo";
import { productAvailability, productDescription } from "@/lib/seoPages";
import Reveal from "@/components/Reveal";
import DiscordIcon from "@/components/DiscordIcon";
import TrustBadges from "@/components/TrustBadges";
import { toast } from "@/components/ui/sonner";
import { OrderDialog } from "@/pages/Shop";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [alertSize, setAlertSize] = useState("");
  const [alertEmail, setAlertEmail] = useState("");
  const [alertSent, setAlertSent] = useState(false);

  useEffect(() => {
    api.get("/products")
      .then((r) => setProduct((r.data || []).find((p) => (p.slug || p.id) === slug) || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="py-24 text-center text-slate-400">Chargement...</div>;
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="product-not-found">
        <h1 className="text-3xl font-extrabold uppercase">Produit introuvable</h1>
        <p className="mt-3 text-slate-400">Ce maillot n'existe pas ou n'est plus en vente.</p>
        <Link to="/boutique" className="mt-6 inline-block rounded-xl bg-orange-500 px-6 py-3 font-heading text-lg font-bold uppercase text-[#060B18] transition-all hover:bg-orange-400">
          Retour à la boutique
        </Link>
      </div>
    );
  }

  const imageUrl = product.image_url?.startsWith("/") ? window.location.origin + product.image_url : product.image_url;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: productDescription(product),
    image: imageUrl ? [imageUrl] : undefined,
    brand: { "@type": "Brand", name: "Emisphere Esport" },
    offers: {
      "@type": "Offer",
      url: window.location.origin + `/boutique/${encodeURIComponent(slug)}`,
      priceCurrency: "EUR",
      price: product.price.toFixed(2),
      availability: productAvailability(product),
      itemCondition: "https://schema.org/NewCondition",
    },
  };

  return (
    <div data-testid="product-detail-page" className="mx-auto max-w-6xl px-4 py-12 sm:px-8 lg:py-16">
      <Seo
        title={product.name}
        description={productDescription(product)}
        path={`/boutique/${encodeURIComponent(slug)}`}
        jsonLd={jsonLd}
      />
      <Link to="/boutique" data-testid="product-back-link"
        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-orange-400 transition-colors hover:text-orange-300">
        <ArrowLeft className="h-4 w-4" /> Retour à la boutique
      </Link>

      <div className="mt-8 grid items-start gap-10 lg:grid-cols-2">
        <Reveal>
          <div className="card-tactical overflow-hidden bg-[#0B132B]">
            <img src={product.image_url} alt={`${product.name} — maillot officiel Emisphere Esport`} data-testid="product-detail-image"
              className="max-h-[560px] w-full object-contain" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div>
            {product.badge && (
              <span className="rounded-md border border-orange-500/40 bg-orange-500/10 px-2 py-1 text-[11px] font-bold uppercase tracking-widest text-orange-400">
                {product.badge}
              </span>
            )}
            <h1 className="mt-3 text-3xl font-extrabold uppercase tracking-tight sm:text-4xl" data-testid="product-detail-name">{product.name}</h1>
            <p className="mt-2 font-mono2 text-3xl font-black text-orange-500">{product.price.toFixed(2)} €</p>
            <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-400">{product.description}</p>

            {product.show_stock !== false && (
              <div className="mt-6 flex items-center gap-2 text-sm">
                <Package className="h-4 w-4 text-orange-500" />
                {product.stock > 0 ? (
                  <span data-testid="product-detail-stock" className={product.stock <= 5 ? "font-bold text-orange-400" : "text-slate-300"}>
                    {product.stock} exemplaire(s) restant(s)
                  </span>
                ) : product.allow_backorder ? (
                  <span data-testid="product-detail-stock" className="font-bold text-orange-400">Précommande ouverte — expédié après réassort</span>
                ) : (
                  <span data-testid="product-detail-stock" className="font-bold text-red-400">Rupture de stock</span>
                )}
              </div>
            )}

            <button onClick={() => setSelected(product)} disabled={product.stock <= 0 && !product.allow_backorder}
              data-testid="product-detail-buy-button"
              className="glow-orange mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-4 font-heading text-xl font-bold uppercase tracking-wide text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:px-10">
              <ShoppingCart className="h-5 w-5" /> {product.stock <= 0 && product.allow_backorder ? "Précommander" : "Ajouter au panier"}
            </button>
            <TrustBadges />
            <p className="mt-3 text-[11px] text-slate-500">Flocage pseudo personnalisé disponible.</p>

            {!product.allow_backorder && (() => {
              const outSizes = (product.sizes || []).filter((s) => ((product.stock_by_size || {})[s] ?? 0) <= 0);
              if (outSizes.length === 0) return null;
              return (
                <div className="mt-6 rounded-xl border border-orange-500/20 bg-orange-500/5 p-4" data-testid="stock-alert-block">
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-orange-400">
                    <BellRing className="h-4 w-4" /> Prévenez-moi du retour en stock
                  </p>
                  {alertSent ? (
                    <p className="mt-2 text-sm font-semibold text-emerald-400" data-testid="stock-alert-success">
                      C'est noté ! Vous recevrez un email dès le réassort.
                    </p>
                  ) : (
                    <form className="mt-3 flex flex-wrap gap-2" onSubmit={async (e) => {
                      e.preventDefault();
                      try {
                        await api.post("/stock-alerts", { product_id: product.id, size: alertSize, email: alertEmail });
                        setAlertSent(true);
                      } catch (err) { toast.error(errMsg(err)); }
                    }}>
                      <select value={alertSize} onChange={(e) => setAlertSize(e.target.value)} required
                        data-testid="stock-alert-size"
                        className="rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500">
                        <option value="" disabled>Taille épuisée…</option>
                        {outSizes.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <input type="email" placeholder="votre@email.fr" value={alertEmail} required
                        data-testid="stock-alert-email" onChange={(e) => setAlertEmail(e.target.value)}
                        className="min-w-0 flex-1 rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500" />
                      <button type="submit" data-testid="stock-alert-submit"
                        className="rounded-lg bg-orange-500 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#060B18] transition-all hover:bg-orange-400">
                        M'alerter
                      </button>
                    </form>
                  )}
                </div>
              );
            })()}

            <div className="mt-6 border-t border-orange-500/10 pt-5">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                <Share2 className="h-4 w-4 text-orange-500" /> Partager ce maillot
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${product.name} — maillot officiel Emisphere Esport (${product.price.toFixed(2)} €)`)}&url=${encodeURIComponent(window.location.href)}`}
                  target="_blank" rel="noreferrer" data-testid="product-share-x"
                  className="flex items-center gap-2 rounded-lg border border-slate-600 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-300 transition-all hover:border-orange-500 hover:text-orange-400"
                >
                  <Twitter className="h-4 w-4" /> X
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${product.name} — maillot officiel Emisphere Esport (${product.price.toFixed(2)} €) → ${window.location.href}`);
                    toast.success("Message copié — collez-le sur Discord !");
                  }}
                  data-testid="product-share-discord"
                  className="flex items-center gap-2 rounded-lg border border-slate-600 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-300 transition-all hover:border-orange-500 hover:text-orange-400"
                >
                  <DiscordIcon className="h-4 w-4" /> Discord
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success("Lien copié !");
                  }}
                  data-testid="product-share-copy"
                  className="flex items-center gap-2 rounded-lg border border-slate-600 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-300 transition-all hover:border-orange-500 hover:text-orange-400"
                >
                  <Link2 className="h-4 w-4" /> Copier le lien
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {selected && <OrderDialog product={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
